// Datos del Informe Gerencial en PowerPoint (estructura espejo del deck del
// cliente — Robotpack 2026-09).
//
// El PPTX necesita granularidad que el loader base no trae: productividad por
// ÁREA × MES, líderes por LÍNEA (dos períodos lado a lado) y paradas por
// LÍNEA × MES. Ese fetch es LAZY: corre solo al pulsar "Descargar
// (PowerPoint)", con el helper de concurrencia existente (límite 4) y
// progreso visible. Áreas, líneas y meses salen de los DATOS del tenant —
// nada hardcodeado.
//
// Todo se DIGIERE acá a estructuras planas: el generador (y el smoke test)
// consumen estos tipos chicos, no los payloads crudos de los endpoints.
// Una celda/slide sin datos viaja como null → slide con estado vacío.

import type {
  CoordinadorAnalisisParos,
  CoordinadorAnalisisPersonal,
  CoordinadorCumplimiento,
} from 'schemas';
import {
  conLimite,
  getCumplimientoArea,
  getParosLinea,
  getPersonalLinea,
  resumenCostosInforme,
  type DatosInformeGerencial,
  type Periodo,
} from './informeGerencialDatos';

// =============================================================================
// Meses del rango
// =============================================================================

export interface MesInforme {
  /** 'YYYY-MM'. */
  key: string;
  /** 'Junio 2026'. */
  label: string;
  /** Rango del mes CLIPEADO al período seleccionado. */
  periodo: Periodo;
}

const MESES_LARGOS = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

/** Cap de meses para las secciones granulares (evita decks de 100+ slides). */
export const MAX_MESES_PPTX = 4;

/** Meses calendario que intersectan el rango, clipeados. Cap: últimos 4. */
export function mesesDelRango(p: Periodo): MesInforme[] {
  const [y0, m0] = p.desde.split('-').map(Number);
  const [y1, m1] = p.hasta.split('-').map(Number);
  if (!y0 || !m0 || !y1 || !m1) return [];
  const meses: MesInforme[] = [];
  let y = y0;
  let m = m0;
  while (y < y1 || (y === y1 && m <= m1)) {
    const key = `${y}-${String(m).padStart(2, '0')}`;
    const primero = `${key}-01`;
    const ultimo = `${key}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`;
    meses.push({
      key,
      label: `${MESES_LARGOS[m - 1]} ${y}`,
      periodo: {
        desde: primero < p.desde ? p.desde : primero,
        hasta: ultimo > p.hasta ? p.hasta : ultimo,
      },
    });
    m++;
    if (m > 12) {
      m = 1;
      y++;
    }
  }
  return meses.slice(-MAX_MESES_PPTX);
}

// =============================================================================
// Tipos digeridos (fixture-friendly — el smoke test los arma a mano)
// =============================================================================

export interface PptxFacturacion {
  /** Mes → valor por área (millones COP) — de resumen_areas agrupado por q_key. */
  meses: Array<{ key: string; label: string; porArea: Record<string, number>; totalM: number }>;
  areas: string[];
  metaMensualMillones: number | null;
  acumuladoM: number;
  unidades: number;
  ops: number;
}

export interface PptxNivel {
  semanas: Array<{
    label: string;
    programadas: number;
    realizadas: number;
    cumplAcido: number | null;
    cumplReal: number | null;
  }>;
  /** Pivot área × semana del cumplimiento REAL (%). null = sin dato. */
  areas: string[];
  matriz: Array<Array<number | null>>;
  acumulado: { cumplReal: number | null; cumplAcido: number | null; prog: number; real: number };
}

export interface PptxProductividadCelda {
  areaNombre: string;
  mesLabel: string;
  porHora: Array<{ label: string; cantidad: number; cumplimiento: number | null }>;
  porTurno: Array<{ label: string; cantidad: number; cumplimiento: number | null }>;
  lineas: Array<{
    codigo: string;
    producido: number;
    meta: number;
    pct: number | null;
    horasTrab: number;
    horasParo: number;
    topCausal: string | null;
  }>;
  producido: number;
  cumplPct: number | null;
  horasParo: number;
  utilidadM: number | null;
}

export interface PptxLideresMes {
  mesLabel: string;
  lideres: Array<{
    nombre: string;
    producido: number;
    esperado: number;
    pct: number | null;
    horas: number;
    dias: number;
  }>;
}

export interface PptxLideresLinea {
  lineaLabel: string;
  /** 1 o 2 meses (los últimos del rango), lado a lado. null = sin datos del mes. */
  meses: Array<PptxLideresMes | null>;
}

export interface PptxParosCelda {
  lineaLabel: string;
  mesLabel: string;
  categorias: Array<{
    nombre: string;
    color: string | null;
    causales: Array<{
      nombre: string;
      eventos: number;
      minutos: number;
      pct: number;
      perdida: number | null;
    }>;
  }>;
  totalMin: number;
  totalEv: number;
  totalPerdida: number | null;
}

export interface PptxCostos {
  valorM: number | null;
  costoMoM: number | null;
  utilidadM: number | null;
  perdidaParosM: number | null;
  /** Áreas de líneas + (si hay) una fila "Tiendas" — consolidado como el directivo. */
  porArea: Array<{ nombre: string; valorM: number; moM: number; utilidadM: number }>;
  /** 'producción validada' | 'producción capturada' — lo que realmente se usó. */
  baseLabel?: string;
  /** DN9: producción sin precio, fuera del cálculo y declarada. null = todo con tarifa. */
  sinTarifa?: { paquetes: number; skus: number; costoMoM: number } | null;
  /** Notas listas (base + conciliación validado/pendiente/capturado; exclusiones). */
  notaBase?: string;
  notaExclusiones?: string;
}

/** Slot de slide granular: SIEMPRE lleva etiquetas (para el estado vacío). */
export interface SlotProductividad {
  areaNombre: string;
  mesLabel: string;
  datos: PptxProductividadCelda | null;
}
export interface SlotParos {
  lineaLabel: string;
  mesLabel: string;
  datos: PptxParosCelda | null;
}

/** Entrada completa del generador PPTX (todo digerido, sin fetch adentro). */
export interface PptxInput {
  tenantNombre: string;
  plantaLabel: string;
  periodoLabel: string;
  generadoPor: string;
  incluirRrhh: boolean;
  facturacion: PptxFacturacion | null;
  nivel: PptxNivel | null;
  /** Un slot por (área × mes), en orden área → mes. datos null = estado vacío. */
  productividad: SlotProductividad[];
  lideres: PptxLideresLinea[];
  /** Un slot por (línea × mes). datos null = estado vacío. */
  paros: SlotParos[];
  costos: PptxCostos | null;
}

// =============================================================================
// Digests de los payloads base
// =============================================================================

const M = (n: number): number => Math.round((n / 1_000_000) * 10) / 10;

/** Primer nombre + primer apellido — misma convención que la TV de planta. */
function nombreCortoLider(nombre: string, apellido: string): string {
  const primero = (s: string): string => s.trim().split(/\s+/)[0] ?? '';
  return `${primero(nombre)} ${primero(apellido)}`.trim();
}

function etiquetaMes(key: string): string {
  const [y, m] = key.split('-').map(Number);
  return `${MESES_LARGOS[(m ?? 1) - 1]} ${y}`;
}

/** "Sem. del 1 jun" desde semana_iso (lunes ISO clásico — misma nota del PDF). */
export function etiquetaSemanaCorta(semanaIso: string, inicioIso: string): string {
  const [, m, d] = inicioIso.split('-').map(Number);
  const cortos = MESES_LARGOS.map((x) => x.slice(0, 3).toLowerCase());
  return d && m ? `Sem. del ${d} ${cortos[m - 1]}` : semanaIso;
}

export function digestFacturacion(base: DatosInformeGerencial): PptxFacturacion | null {
  if (!base.facturacion.ok) return null;
  const f = base.facturacion.data;
  const porMes = new Map<string, Record<string, number>>();
  const areasSet = new Set<string>();
  for (const ra of f.resumen_areas) {
    const mes = ra.q_key.slice(0, 7);
    const acc = porMes.get(mes) ?? {};
    acc[ra.area_nombre] = (acc[ra.area_nombre] ?? 0) + ra.valor_total;
    areasSet.add(ra.area_nombre);
    porMes.set(mes, acc);
  }
  const areas = [...areasSet].sort();
  const meses = [...porMes.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([key, porAreaRaw]) => {
      const porArea: Record<string, number> = {};
      let total = 0;
      for (const a of areas) {
        porArea[a] = M(porAreaRaw[a] ?? 0);
        total += porAreaRaw[a] ?? 0;
      }
      return { key, label: etiquetaMes(key), porArea, totalM: M(total) };
    });
  return {
    meses,
    areas,
    metaMensualMillones: f.meta_facturacion_mensual_millones,
    acumuladoM: M(f.totales.valor_total),
    unidades: f.totales.unidades_validadas,
    ops: f.totales.ops,
  };
}

export function digestNivel(
  base: DatosInformeGerencial,
  inicioDeSemana: (semanaIso: string) => string,
): PptxNivel | null {
  if (!base.nivel.ok) return null;
  const n = base.nivel.data;
  const semanas = n.semanas.slice(-10);
  const clavesSemana = semanas.map((s) => s.semana_iso);
  const areas = [...new Set(n.filas.map((f) => f.area_nombre))].sort();
  const matriz = areas.map((area) =>
    clavesSemana.map((sem) => {
      const fila = n.filas.find((f) => f.area_nombre === area && f.semana_iso === sem);
      return fila ? fila.cumplimiento_real_pct : null;
    }),
  );
  return {
    semanas: semanas.map((s) => ({
      label: etiquetaSemanaCorta(s.semana_iso, inicioDeSemana(s.semana_iso)),
      programadas: s.programadas,
      realizadas: s.realizadas,
      cumplAcido: s.cumplimiento_pct,
      cumplReal: s.cumplimiento_real_pct,
    })),
    areas,
    matriz,
    acumulado: {
      cumplReal: n.acumulado.cumplimiento_real_pct,
      cumplAcido: n.acumulado.cumplimiento_pct,
      prog: n.acumulado.programadas,
      real: n.acumulado.realizadas,
    },
  };
}

export function digestCostos(base: DatosInformeGerencial): PptxCostos | null {
  if (!base.cumplimiento.ok) return null;
  // MISMA lectura que el PDF (resumenCostosInforme): consolidado líneas + tiendas,
  // base declarada y conciliación validado/pendiente/capturado.
  const res = resumenCostosInforme(base.cumplimiento.data);
  if (!res) return null;
  const perdida = base.lineas.ok ? (base.lineas.data.totales.perdida_pesos_total ?? null) : null;
  return {
    valorM: M(res.valor_producido),
    costoMoM: M(res.costo_mano_obra),
    utilidadM: M(res.utilidad),
    perdidaParosM: perdida == null ? null : M(perdida),
    porArea: res.filas.map((a) => ({
      nombre: a.nombre,
      valorM: M(a.valor_producido),
      moM: M(a.costo_mano_obra),
      utilidadM: M(a.utilidad),
    })),
    baseLabel: res.baseLabel,
    sinTarifa:
      res.sin_tarifa.paquetes > 0
        ? {
            paquetes: res.sin_tarifa.paquetes,
            skus: res.sin_tarifa.skus,
            costoMoM: M(res.sin_tarifa.costo_mano_obra),
          }
        : null,
    notaBase: res.nota_base,
    notaExclusiones: res.nota_exclusiones,
  };
}

function digestProductividad(
  areaNombre: string,
  mesLabel: string,
  c: CoordinadorCumplimiento,
): PptxProductividadCelda {
  return {
    areaNombre,
    mesLabel,
    porHora: c.produccion_por_hora.map((h) => ({
      label: `${String(h.hora).padStart(2, '0')}:00`,
      cantidad: h.cantidad_real,
      cumplimiento: h.cumplimiento_meta_pct,
    })),
    porTurno: c.produccion_por_turno.map((t) => ({
      label: t.turno_nombre,
      cantidad: t.cantidad_real,
      cumplimiento: t.cumplimiento_meta_pct,
    })),
    lineas: c.filas
      .map((f) => ({
        codigo: f.linea_codigo,
        producido: f.cantidad_real,
        meta: f.meta_acumulada,
        pct: f.cumplimiento_meta_pct,
        horasTrab: f.horas_trabajadas,
        horasParo: f.horas_paro,
        topCausal: f.top_causal_nombre,
      }))
      .sort((a, b) => (b.pct ?? -1) - (a.pct ?? -1)),
    producido: c.totales.cantidad_real_total,
    cumplPct: c.totales.cumplimiento_meta_promedio_pct,
    horasParo: Math.round((c.totales.minutos_paro_total / 60) * 10) / 10,
    utilidadM: c.totales.utilidad_total == null ? null : M(c.totales.utilidad_total),
  };
}

function digestLideresMes(mesLabel: string, p: CoordinadorAnalisisPersonal): PptxLideresMes {
  return {
    mesLabel,
    lideres: p.lideres
      .filter((l) => l.bloques_capturados > 0)
      .map((l) => ({
        // Nombre corto (primer nombre + primer apellido), como la TV de planta:
        // el nombre completo desborda la columna y envuelve cada fila.
        nombre: nombreCortoLider(l.nombre, l.apellido),
        producido: l.cantidad_real,
        esperado: l.esperado_capacidad,
        pct: l.cumplimiento_meta_pct,
        horas: l.bloques_capturados,
        dias: l.dias_capturados,
      }))
      .sort((a, b) => (b.pct ?? -1) - (a.pct ?? -1)),
  };
}

function digestParos(
  lineaLabel: string,
  mesLabel: string,
  p: CoordinadorAnalisisParos,
): PptxParosCelda {
  const porCategoria = new Map<string, PptxParosCelda['categorias'][number]>();
  for (const c of p.por_causal_especifica) {
    const cat = porCategoria.get(c.categoria_nombre) ?? {
      nombre: c.categoria_nombre,
      color: c.categoria_color,
      causales: [],
    };
    cat.causales.push({
      nombre: c.causal_nombre,
      eventos: c.paros_count,
      minutos: c.minutos_totales,
      pct: c.pct_minutos,
      perdida: c.perdida_pesos,
    });
    porCategoria.set(c.categoria_nombre, cat);
  }
  const categorias = [...porCategoria.values()]
    .map((cat) => ({
      ...cat,
      causales: cat.causales.sort((a, b) => b.minutos - a.minutos),
    }))
    .sort(
      (a, b) =>
        b.causales.reduce((s, x) => s + x.minutos, 0) -
        a.causales.reduce((s, x) => s + x.minutos, 0),
    );
  const perdidas = p.por_causal_especifica.map((c) => c.perdida_pesos).filter((x) => x !== null);
  return {
    lineaLabel,
    mesLabel,
    categorias,
    totalMin: p.totales.minutos_totales,
    totalEv: p.totales.paros_count,
    totalPerdida: perdidas.length > 0 ? perdidas.reduce((s, x) => s + x, 0) : null,
  };
}

// =============================================================================
// Fetch granular LAZY (al pulsar "Descargar (PowerPoint)")
// =============================================================================

export interface RecursosPptx {
  areas: Array<{ id: string; nombre: string }>;
  lineas: Array<{ id: string; codigo: string; nombre: string; area_id: string }>;
}

/** Áreas y líneas REALES del tenant, desde los recursos ya cargados. */
export function extraerRecursos(base: DatosInformeGerencial): RecursosPptx {
  const fuente = base.cumplimiento.ok
    ? base.cumplimiento.data.recursos
    : base.paros.ok
      ? base.paros.data.recursos
      : null;
  if (!fuente) return { areas: [], lineas: [] };
  const lineas = fuente.lineas.map((l) => ({
    id: l.id,
    codigo: l.codigo,
    nombre: l.nombre,
    area_id: l.area_id,
  }));
  // Solo áreas con al menos una línea activa (evita slides vacíos de áreas muertas).
  const conLineas = new Set(lineas.map((l) => l.area_id));
  return {
    areas: fuente.areas
      .filter((a) => conLineas.has(a.id))
      .map((a) => ({ id: a.id, nombre: a.nombre })),
    lineas,
  };
}

export interface DatosGranularesPptx {
  productividad: SlotProductividad[];
  lideres: PptxLideresLinea[];
  paros: SlotParos[];
}

/**
 * Fetch granular con concurrencia 4 y progreso. Llamadas en el peor caso:
 * áreas×meses (cumplimiento) + líneas×min(meses,2) (personal) + líneas×meses
 * (paros), con meses cap a MAX_MESES_PPTX. Una llamada fallida deja su celda
 * en null (slide con estado vacío) — nunca tumba el deck.
 */
export async function cargarDatosGranulares(
  recursos: RecursosPptx,
  meses: MesInforme[],
  onProgreso: (msg: string) => void,
): Promise<DatosGranularesPptx> {
  const { areas, lineas } = recursos;
  const mesesLideres = meses.slice(-2);

  const productividad: Array<PptxProductividadCelda | null> = new Array(
    areas.length * meses.length,
  ).fill(null);
  const lideresTablas: Array<PptxLideresMes | null> = new Array(
    lineas.length * mesesLideres.length,
  ).fill(null);
  const paros: Array<PptxParosCelda | null> = new Array(lineas.length * meses.length).fill(null);

  const tareas: Array<() => Promise<void>> = [];
  areas.forEach((area, ai) => {
    meses.forEach((mes, mi) => {
      tareas.push(async () => {
        const r = await getCumplimientoArea(mes.periodo, area.id);
        if (r.ok) {
          productividad[ai * meses.length + mi] = digestProductividad(
            area.nombre,
            mes.label,
            r.data,
          );
        }
      });
    });
  });
  lineas.forEach((linea, li) => {
    mesesLideres.forEach((mes, mi) => {
      tareas.push(async () => {
        const r = await getPersonalLinea(mes.periodo, linea.id);
        if (r.ok) {
          lideresTablas[li * mesesLideres.length + mi] = digestLideresMes(mes.label, r.data);
        }
      });
    });
    meses.forEach((mes, mi) => {
      tareas.push(async () => {
        const r = await getParosLinea(mes.periodo, linea.id);
        if (r.ok) {
          paros[li * meses.length + mi] = digestParos(
            `${linea.codigo} · ${linea.nombre}`,
            mes.label,
            r.data,
          );
        }
      });
    });
  });

  let hechas = 0;
  const total = tareas.length;
  const conProgreso = tareas.map((t) => async () => {
    await t();
    hechas++;
    onProgreso(`Cargando datos del deck (${hechas} de ${total})…`);
  });
  await conLimite(conProgreso, 4);

  return {
    productividad: areas.flatMap((area, ai) =>
      meses.map((mes, mi) => ({
        areaNombre: area.nombre,
        mesLabel: mes.label,
        datos: productividad[ai * meses.length + mi] ?? null,
      })),
    ),
    lideres: lineas.map((linea, li) => ({
      lineaLabel: `${linea.codigo} · ${linea.nombre}`,
      meses: mesesLideres.map((_, mi) => lideresTablas[li * mesesLideres.length + mi] ?? null),
    })),
    paros: lineas.flatMap((linea, li) =>
      meses.map((mes, mi) => ({
        lineaLabel: `${linea.codigo} · ${linea.nombre}`,
        mesLabel: mes.label,
        datos: paros[li * meses.length + mi] ?? null,
      })),
    ),
  };
}
